import * as Sentry from '@sentry/react';
import type { Player } from 'video.js';
import type MediaError from 'video.js/dist/types/media-error';

export function captureVideoJsError(
  error: MediaError | null | undefined,
  sentryErrorSource: string,
  metadata?: { [key: string]: object }
) {
  if (error) {
    let videoJsFormattedError: Error | MediaError = error;

    if (!(error instanceof Error)) {
      // if this catches, it means that the error is not a stringifiable object
      videoJsFormattedError = new Error(`video.js: ${JSON.stringify(error)}`);
    }

    const errorTags = Object.keys(metadata ?? {}).reduce((acc, value) => {
      // Sentry tag character max limit is 200.
      acc[value] = JSON.stringify(metadata?.[value]).slice(0, 200);
      return acc;
    }, {} as { [key: string]: string });

    Sentry.captureException(videoJsFormattedError, {
      tags: { source: sentryErrorSource, errorType: 'media', ...errorTags },
    });
  }
}

/**
 * Handles video.js player errors by capturing them in Sentry with metadata
 * @param player - The video.js Player instance that may have an error
 * @param sentryErrorSource - String identifier for the error source to be used as a Sentry tag
 * @param metadata - Optional object containing additional metadata to be included as Sentry tags
 */
export function handleVideoJsError(
  player: Player | null,
  sentryErrorSource: string,
  metadata?: { [key: string]: object }
) {
  const error = player?.error();
  captureVideoJsError(error, sentryErrorSource, metadata);
}
